'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, History, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { AppContentCurrentEntity, AppContentVersionEntity } from '@/features/app-content/domain/entities/app-content.entity';
import type { LegalAdminDocumentKind } from '@/features/admin/constants/legal-admin-document-kind';
import {
  LEGAL_ADMIN_PUBLISH_CHANGE_NOTES,
  LEGAL_ADMIN_UI,
} from '@/features/admin/constants/legal-admin-ui-copy';
import {
  LEGAL_ADMIN_DEFAULT_PRIVACY,
  LEGAL_ADMIN_DEFAULT_TERMS,
} from '@/features/admin/constants/legal-admin-default-content';
import type { LegalAdminVersionRecord } from '@/features/admin/domain/legal-admin-version';
import { useAppContent } from '@/features/app-content/presentation/context/AppContentContext';
import { SectionHeader } from '@/features/admin/presentation/components/SectionHeader';
import { Button } from '@/core/presentation/components/ui/Button';
import { Badge } from '@/core/presentation/components/ui/Badge';
import { Label } from '@/core/presentation/components/ui/Label';
import { useToast } from '@/core/presentation/components/Toast';

function formatPt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function versionLabel(n: number): string {
  return `v${n}`;
}

function defaultContentForAdminKind(kind: LegalAdminDocumentKind): string {
  return kind === 'terms' ? LEGAL_ADMIN_DEFAULT_TERMS : LEGAL_ADMIN_DEFAULT_PRIVACY;
}

function mapCurrentToAdminRow(c: AppContentCurrentEntity): LegalAdminVersionRecord {
  const created = c.createdAt ?? new Date(0);
  const updated = c.updatedAt ?? c.publishedAt ?? new Date();
  return {
    id: 'current',
    versionNumber: c.currentVersion,
    content: c.currentContent,
    requiresReacceptance: c.requiresReacceptance ?? false,
    createdAt: created instanceof Date ? created.toISOString() : '',
    updatedAt: updated instanceof Date ? updated.toISOString() : '',
    createdByName: c.createdByName ?? '—',
    updatedByName: c.updatedByName ?? '—',
  };
}

function mapVersionToAdminRow(v: AppContentVersionEntity): LegalAdminVersionRecord {
  const created = v.createdAt ?? v.publishedAt ?? new Date();
  const published = v.publishedAt ?? v.createdAt ?? new Date();
  return {
    id: `v${v.version}`,
    versionNumber: v.version,
    content: v.content,
    requiresReacceptance: v.requiresReacceptance,
    createdAt: created instanceof Date ? created.toISOString() : String(created),
    updatedAt: published instanceof Date ? published.toISOString() : String(published),
    createdByName: v.createdByName ?? '—',
    updatedByName: v.createdByName ?? '—',
  };
}

function HistoryVersionCard({ record }: { record: LegalAdminVersionRecord }) {
  const [open, setOpen] = useState(false);
  const badge =
    record.requiresReacceptance ? LEGAL_ADMIN_UI.BADGE_REQUIRES_REACCEPT : LEGAL_ADMIN_UI.BADGE_NO_REACCEPT;

  return (
    <div className="rounded-xl border border-admin-border bg-[#2a3142] text-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">
            {versionLabel(record.versionNumber)}
            <span className="ml-2 font-normal text-white/50">· {formatPt(record.updatedAt)}</span>
          </p>
          <p className="mt-0.5 text-xs text-white/45">
            {open ? LEGAL_ADMIN_UI.HISTORY_TOGGLE_CLOSE : LEGAL_ADMIN_UI.HISTORY_TOGGLE_OPEN}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="outline" className="border-white/20 text-white/80">
            {badge}
          </Badge>
          {open ?
            <ChevronUp className="h-5 w-5 text-white/60" aria-hidden />
          : <ChevronDown className="h-5 w-5 text-white/60" aria-hidden />}
        </div>
      </button>
      {open ?
        <div className="border-t border-white/10 px-4 pb-4 pt-3">
          <div className="rounded-lg border border-admin-border bg-white p-4 text-sm text-text-body whitespace-pre-wrap max-h-[min(70vh,520px)] overflow-y-auto font-mono leading-relaxed">
            {record.content}
          </div>
        </div>
      : null}
    </div>
  );
}

function VersionMetaGrid({ record }: { record: LegalAdminVersionRecord }) {
  return (
    <div className="mt-4 grid grid-cols-1 gap-3 text-xs text-white/70 sm:grid-cols-2 sm:gap-x-8">
      <div>
        <p>
          <span className="text-white/45">{LEGAL_ADMIN_UI.CREATED_AT}</span>{' '}
          <span className="text-white/90">{formatPt(record.createdAt)}</span>
        </p>
        <p className="mt-1">
          <span className="text-white/45">{LEGAL_ADMIN_UI.UPDATED_AT}</span>{' '}
          <span className="text-white/90">{formatPt(record.updatedAt)}</span>
        </p>
      </div>
      <div>
        <p>
          <span className="text-white/45">{LEGAL_ADMIN_UI.CREATED_BY}</span>{' '}
          <span className="text-white/90">{record.createdByName}</span>
        </p>
        <p className="mt-1">
          <span className="text-white/45">{LEGAL_ADMIN_UI.UPDATED_BY}</span>{' '}
          <span className="text-white/90">{record.updatedByName}</span>
        </p>
      </div>
    </div>
  );
}

function CurrentVersionPanel({ record }: { record: LegalAdminVersionRecord }) {
  const badge =
    record.requiresReacceptance ? LEGAL_ADMIN_UI.BADGE_REQUIRES_REACCEPT : LEGAL_ADMIN_UI.BADGE_NO_REACCEPT;

  return (
    <section
      className="rounded-xl border border-admin-border bg-[#2a3142] p-5 text-white shadow-md"
      aria-labelledby="legal-current-version-heading"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <h2 id="legal-current-version-heading" className="text-base font-semibold text-white">
          {LEGAL_ADMIN_UI.CURRENT_VERSION_PREFIX}{' '}
          <span className="text-cyan">{versionLabel(record.versionNumber)}</span>
        </h2>
        <Badge variant="outline" className="w-fit shrink-0 border-white/20 text-white/80">
          {badge}
        </Badge>
      </div>
      <VersionMetaGrid record={record} />
      <div className="mt-5">
        <Label className="text-xs text-white/50">{LEGAL_ADMIN_UI.CONTENT_LABEL}</Label>
        <div className="mt-2 rounded-lg border border-admin-border bg-white p-4 text-sm text-text-body whitespace-pre-wrap max-h-[min(70vh,560px)] overflow-y-auto font-mono leading-relaxed">
          {record.content}
        </div>
      </div>
    </section>
  );
}

function DraftVersionPanel({
  nextVersionNumber,
  content,
  onChange,
  onSave,
  onCancel,
  saveBusy,
}: {
  nextVersionNumber: number;
  content: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saveBusy: boolean;
}) {
  return (
    <section
      className="rounded-xl border-2 border-cyan/50 bg-[#252b3a] p-5 text-white shadow-lg ring-1 ring-cyan/20"
      aria-label={LEGAL_ADMIN_UI.NEW_VERSION_TITLE(versionLabel(nextVersionNumber))}
    >
      <h2 className="text-base font-semibold text-cyan">
        {LEGAL_ADMIN_UI.NEW_VERSION_TITLE(versionLabel(nextVersionNumber))}
      </h2>
      <p className="mt-1 text-xs text-white/55">{LEGAL_ADMIN_UI.DRAFT_HINT}</p>
      <div className="mt-4">
        <Label htmlFor="legal-draft-content" className="text-xs text-white/50">
          {LEGAL_ADMIN_UI.CONTENT_LABEL}
        </Label>
        <textarea
          id="legal-draft-content"
          value={content}
          onChange={(e) => onChange(e.target.value)}
          rows={16}
          className="mt-2 w-full resize-y rounded-lg border border-admin-border bg-white px-3 py-3 text-sm text-text-body focus:outline-none focus:ring-2 focus:ring-cyan/40 min-h-[200px] font-mono leading-relaxed"
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          className="bg-admin-navy hover:bg-admin-navy-mid text-white"
          onClick={onSave}
          disabled={saveBusy}
          loading={saveBusy}
          loadingLabel={LEGAL_ADMIN_UI.SAVE_LOADING}
        >
          <Save className="h-4 w-4 mr-1.5" />
          {LEGAL_ADMIN_UI.SAVE}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="border-white/25 text-white hover:bg-white/10"
          onClick={onCancel}
          disabled={saveBusy}
        >
          <X className="h-4 w-4 mr-1.5" />
          {LEGAL_ADMIN_UI.CANCEL}
        </Button>
      </div>
    </section>
  );
}

export interface LegalDocumentAdminPageProps {
  kind: LegalAdminDocumentKind;
  title: string;
  subtitle: string;
  editorDisplayName: string;
}

export function LegalDocumentAdminPage({ kind, title, subtitle, editorDisplayName }: LegalDocumentAdminPageProps) {
  const toast = useToast();
  const { state, events } = useAppContent();
  const [draftContent, setDraftContent] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const slot = kind === 'terms' ? state.terms : state.privacy;

  useEffect(() => {
    if (kind === 'terms') {
      void events.refreshTerms();
    } else {
      void events.refreshPrivacy();
    }
  }, [kind, events.refreshTerms, events.refreshPrivacy]);

  const currentRecord = useMemo(
    () => (slot.current ? mapCurrentToAdminRow(slot.current) : null),
    [slot.current]
  );

  const previousRows = useMemo(
    () => slot.versions.map((v) => mapVersionToAdminRow(v)),
    [slot.versions]
  );

  const nextVersionNumber = (slot.current?.currentVersion ?? 0) + 1;
  const hasDraft = draftContent !== null;

  const openNewVersionDraft = useCallback(() => {
    const base = currentRecord?.content ?? defaultContentForAdminKind(kind);
    setDraftContent(base);
  }, [currentRecord, kind]);

  const cancelDraft = useCallback(() => {
    setDraftContent(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (draftContent == null) return;
    const dto = {
      content: draftContent,
      changeNotes: LEGAL_ADMIN_PUBLISH_CHANGE_NOTES,
      requiresReacceptance: false,
      updatedByUid: null,
      updatedByName: editorDisplayName,
    };
    setPublishing(true);
    try {
      const result =
        kind === 'terms' ? await events.updateTerms(dto) : await events.updatePrivacy(dto);
      if (result.success) {
        toast.success(LEGAL_ADMIN_UI.TOAST_SAVED);
        setDraftContent(null);
      } else {
        toast.error(result.error ?? 'Falha ao publicar.');
      }
    } finally {
      setPublishing(false);
    }
  }, [draftContent, kind, events, editorDisplayName, toast]);

  if (slot.loading && !slot.current && !hasDraft) {
    return <p className="text-sm text-admin-text-secondary">Carregando…</p>;
  }

  if (slot.error && !slot.current && !hasDraft) {
    return (
      <div>
        <SectionHeader title={title} subtitle={subtitle} />
        <p className="mt-4 text-sm text-red-300">{slot.error}</p>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-admin-border text-admin-text hover:bg-admin-bg-block"
              disabled={hasDraft}
              onClick={openNewVersionDraft}
            >
              <Plus className="h-4 w-4 mr-1" />
              {LEGAL_ADMIN_UI.NEW_VERSION}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-admin-border text-admin-text hover:bg-admin-bg-block"
              onClick={() => setHistoryOpen((h) => !h)}
            >
              <History className="h-4 w-4 mr-1" />
              {historyOpen ? LEGAL_ADMIN_UI.HIDE_HISTORY : LEGAL_ADMIN_UI.VIEW_HISTORY}
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {hasDraft && draftContent !== null ?
          <DraftVersionPanel
            nextVersionNumber={nextVersionNumber}
            content={draftContent}
            onChange={setDraftContent}
            onSave={() => void handleSave()}
            onCancel={cancelDraft}
            saveBusy={publishing}
          />
        : null}

        {!currentRecord ?
          <section className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5 text-sm text-white/90">
            <p className="font-medium text-white">Nenhuma versão publicada no Firestore ainda.</p>
            <p className="mt-2 text-white/75">
              Use <strong className="text-white">Nova versão</strong> para criar o primeiro documento em{' '}
              <code className="rounded bg-black/30 px-1 py-0.5 text-xs">
                AppContent/{kind === 'terms' ? 'termsOfUse' : 'privacyPolicy'}
              </code>
              .
            </p>
          </section>
        : (
          <CurrentVersionPanel record={currentRecord} />
        )}

        {historyOpen ?
          <div className="rounded-xl border border-admin-border bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-admin-text mb-3">{LEGAL_ADMIN_UI.HISTORY_TITLE}</h3>
            {previousRows.length === 0 ?
              <p className="text-sm text-admin-text-secondary py-4">{LEGAL_ADMIN_UI.HISTORY_EMPTY}</p>
            : <div className="space-y-3">
                {previousRows.map((v) => (
                  <HistoryVersionCard key={v.id} record={v} />
                ))}
              </div>
            }
          </div>
        : null}
      </div>
    </div>
  );
}
